import { AccessibilityManager } from '../utils/AccessibilityManager';
import { ADDIN_NAMESPACE } from '../config/addinNamespace';
import { chevronLeftIcon, crosshairIcon } from '../ui/icons';
import { balanceDateLabel, EntriesViewPanel, hasViews, type ViewsClient } from './EntriesViewPanel';
import { formulaCallName, needsPrefix, type FormulaCompany } from './formulaPrefix';

export interface FormulaParameter {
  name: string;
  required?: boolean;
  linkedField?: string;
  linkedFieldLabel?: string;
  isDateComponent?: boolean;
  dateComponentType?: 'year' | 'month' | 'day';
  parentDateFieldId?: string;
}

export interface Formula {
  id: string;
  name: string;
  displayName: string;
  description: string;
  category: string;
  functionType: string;
  /** 'DEMO' means the formula answers from sample data, not the ERP. */
  erpType?: string | null;
  parameters: string[];
  parametersSchema: { parameters?: FormulaParameter[] };
  companyShortName?: string | null;
  companyName?: string;
  companyType?: 'primary' | 'external' | 'demo';
  companyId?: string;
  /** Whether the formula's company is the session's active one; see formulaPrefix.ts. */
  companyIsActive?: boolean;
  multiCompany?: boolean;
  sourceCompanyName?: string | null;
}

/** Badge text per function type. Still the stored, canonical value — it groups
 *  formulas in the portal and labels them here. It no longer picks the Excel
 *  function, because there is only one for values. */
const FUNCTION_TYPE_LABELS: Record<string, string> = {
  ledger: 'Ledger',
  // Budget and Forecast share the type; the pane is too narrow for "Financial planning".
  budget: 'Plan',
  ar: 'AR',
  ap: 'AP',
};

/** The label on a formula's type badge. */
export function functionTypeLabel(functionType: string): string {
  return FUNCTION_TYPE_LABELS[functionType] || functionType;
}

/**
 * Every formula is called through the same function regardless of what it
 * queries; the name in the first argument is what resolves it. Mapping the type
 * into the function name here would emit BIFROST.BUDGET, which is no longer
 * registered and would spill #NAME?.
 */
export function getFunctionName(_functionType?: string): string {
  return `${ADDIN_NAMESPACE}.LEDGER`;
}

/** Which of the two functions a builder writes: the value, or the rows behind it. */
export type BuilderMode = 'ledger' | 'entries';

export function builderFunctionName(mode: BuilderMode, functionType?: string): string {
  return mode === 'entries' ? `${ADDIN_NAMESPACE}.ENTRIES` : getFunctionName(functionType);
}

/** Excel's own idiom for "this is a reference, not text". */
const REFERENCE_PREFIX = '=';

/**
 * A parameter is either a literal (quoted) or a cell reference (passed through
 * so Excel resolves it). Quoting a reference sends the text "A8" to the backend,
 * which matches no account and silently returns 0 — so the two must not be
 * conflated. Reference mode comes from the cell picker, the A1 toggle, or an
 * `=` prefix typed by the user.
 *
 * A1-style auto-detection is deliberately absent: demo customer numbers like
 * C0048 are indistinguishable from a cell address.
 */
function formatParamValue(val: string | undefined, paramName: string, paramRefs?: Set<string>): string {
  const trimmed = (val ?? '').trim();
  if (!trimmed) return '""';
  if (trimmed.startsWith(REFERENCE_PREFIX)) return trimmed.slice(1).trim();
  if (paramRefs?.has(paramName)) return trimmed;
  return `"${trimmed}"`;
}

/**
 * The arguments a formula takes, in the order a cell passes them: the stored
 * schema order, one argument per parameter. The backend maps arguments to
 * parameters by that position, so the order here must never be re-derived —
 * writing a date group as "year, month" whatever its stored order put the
 * month in the year's place for a formula whose components were swapped, and
 * dropped a day component altogether.
 */
export function formulaArguments(formula: Formula): FormulaParameter[] {
  const schema = formula.parametersSchema?.parameters;
  if (schema && schema.length > 0) return schema;
  return (formula.parameters ?? []).map(name => ({ name }));
}

function formulaCompany(formula: Formula): FormulaCompany {
  return {
    companyId: formula.companyId ?? '',
    shortName: formula.companyShortName ?? null,
    // A formula loaded without company context is the active company's.
    isActive: formula.companyIsActive ?? true,
  };
}

function formulaNameArgument(formula: Formula): string {
  return `"${formulaCallName(formula.name, formulaCompany(formula))}"`;
}

export function buildPreview(
  formula: Formula,
  paramValues: Record<string, string>,
  paramRefs?: Set<string>,
  mode: BuilderMode = 'ledger',
  viewName: string | null = null,
): string {
  const fnName = builderFunctionName(mode, formula.functionType);
  const parts = [
    formulaNameArgument(formula),
    ...formulaArguments(formula).map(p => formatParamValue(paramValues[p.name], p.name, paramRefs)),
    // A saved Entries view is one argument beyond the formula's own.
    ...(mode === 'entries' && viewName ? [`"${viewName.replace(/"/g, '""')}"`] : []),
  ];
  // No padding after the separator: Excel keeps whatever it is given, and the
  // spaces survive into every cell the formula is dragged to.
  return `=${fnName}(${parts.join(',')})`;
}

/** One argument of the preview: its text, and the parameter it belongs to. */
export interface PreviewSlot {
  text: string;
  param: string | null;
  empty: boolean;
}

/**
 * The preview split into arguments, with an empty one shown as its parameter's
 * name rather than `""`. Display only: four `""` in a row say nothing about
 * which is the year and which the account. Insert and copy use buildPreview.
 */
export function previewSlots(
  formula: Formula,
  paramValues: Record<string, string>,
  paramRefs?: Set<string>,
  viewName: string | null = null,
): PreviewSlot[] {
  return [
    { text: formulaNameArgument(formula), param: null, empty: false },
    ...formulaArguments(formula).map(p => {
      const empty = !(paramValues[p.name] ?? '').trim();
      return { text: empty ? p.name : formatParamValue(paramValues[p.name], p.name, paramRefs), param: p.name, empty };
    }),
    ...(viewName ? [{ text: `"${viewName}"`, param: null, empty: false }] : []),
  ];
}

const DATE_COMPONENT_LABELS: Record<string, string> = { year: 'Year', month: 'Month', day: 'Day' };

export function groupDateParameters(
  params: FormulaParameter[]
): Map<string, FormulaParameter[]> {
  const groups = new Map<string, FormulaParameter[]>();
  for (const p of params) {
    if (p.isDateComponent && p.parentDateFieldId) {
      const group = groups.get(p.parentDateFieldId) || [];
      group.push(p);
      groups.set(p.parentDateFieldId, group);
    }
  }
  for (const p of params) {
    if (p.isDateComponent && !p.parentDateFieldId && groups.has(p.name)) {
      groups.get(p.name)?.unshift(p);
    }
  }
  return groups;
}

export class FormulaBuilder {
  private container: HTMLElement;
  private formula: Formula | null = null;
  private paramValues: Record<string, string> = {};
  private paramRefs: Set<string> = new Set();
  private activePickerParam: string | null = null;
  private focusedParam: string | null = null;
  private selectionListenerActive = false;
  private onBack: () => void;
  private mode: BuilderMode;
  private viewsClient: ViewsClient | null;
  private viewName: string | null = null;
  private viewPanel: EntriesViewPanel | null = null;

  constructor(container: HTMLElement, onBack: () => void, mode: BuilderMode = 'ledger', viewsClient: ViewsClient | null = null) {
    this.container = container;
    this.onBack = onBack;
    this.mode = mode;
    this.viewsClient = viewsClient;
  }

  show(formula: Formula): void {
    this.formula = formula;
    this.paramValues = {};
    this.paramRefs = new Set();
    this.activePickerParam = null;
    this.viewName = null;
    this.ensureSelectionListener();
    this.render();
  }

  private ensureSelectionListener(): void {
    if (this.selectionListenerActive) return;
    this.selectionListenerActive = true;

    Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      sheet.onSelectionChanged.add(async (args) => {
        if (this.activePickerParam) {
          const param = this.activePickerParam;
          this.activePickerParam = null;
          this.handleCellSelected(param, args.address);
        }
      });
      await context.sync();
    }).catch(() => {
      this.selectionListenerActive = false;
    });
  }

  /**
   * Input plus its two mode controls: the A1 toggle (literal vs reference) and
   * the crosshair cell picker.
   */
  private inputWrapper(param: string, placeholder: string, extraClass: string, position: number): string {
    const p = this.escapeHtml(param);
    const isRef = this.paramRefs.has(param);
    return `
      <div class="builder-input-wrapper">
        <span class="builder-arg-pos" title="Argument ${position} after the formula name" aria-label="Argument ${position}">${position}</span>
        <input type="text" class="builder-input${extraClass ? ' ' + extraClass : ''}${isRef ? ' has-ref' : ''}" data-param="${p}" placeholder="${placeholder}"${extraClass.includes('date') ? ' inputmode="numeric"' : ''} />
        <button class="builder-mode-btn${isRef ? ' is-ref' : ''}" data-param="${p}" title="${isRef ? 'Treated as a cell reference — click for text' : 'Treated as text — click for a cell reference'}" type="button">${isRef ? 'A1' : '&quot;&quot;'}</button>
        <button class="builder-ref-btn" data-param="${p}" title="Select cell" aria-label="Select cell" type="button">${crosshairIcon}</button>
      </div>`;
  }

  private render(): void {
    if (!this.formula) return;
    const f = this.formula;
    const fnName = builderFunctionName(this.mode, f.functionType);
    const typeBadge = functionTypeLabel(f.functionType);

    let paramsHtml = '';
    const args = formulaArguments(f);
    const dateGroups = groupDateParameters(args);
    const renderedGroups = new Set<string>();

    args.forEach((param, index) => {
      const groupKey = param.isDateComponent ? (param.parentDateFieldId || param.name) : null;
      const group = groupKey ? dateGroups.get(groupKey) : undefined;
      if (groupKey && group) {
        if (renderedGroups.has(groupKey)) return;
        renderedGroups.add(groupKey);
        // Each component keeps its own position badge, so a group whose
        // components are stored month-first, or with a day, reads as such.
        const inputs = args
          .map((p, i) => ({ p, i }))
          .filter(({ p }) => group.includes(p))
          .map(({ p, i }) => this.inputWrapper(p.name, DATE_COMPONENT_LABELS[p.dateComponentType ?? ''] ?? p.name, 'builder-date-input', i + 1))
          .join('');
        paramsHtml += `
          <div class="builder-param-group">
            <label class="builder-param-label">${this.escapeHtml(group[0]?.linkedFieldLabel || 'Date')}</label>
            <div class="builder-date-row">${inputs}</div>
          </div>`;
        return;
      }

      const label = param.linkedFieldLabel || param.name;
      const placeholder = param.name.toLowerCase().includes('account') ? 'e.g. 1000 or 1000:9999' : '';
      paramsHtml += `
        <div class="builder-param-group">
          <label class="builder-param-label">${this.escapeHtml(label)}${param.required ? ' *' : ''}</label>
          ${this.inputWrapper(param.name, placeholder, '', index + 1)}
        </div>`;
    });


    const companyBanner = needsPrefix(formulaCompany(f)) && f.companyName
      ? `<div class="builder-company-banner">Queries data from <strong>${this.escapeHtml(f.companyName)}</strong></div>`
      : '';

    this.container.innerHTML = `
      <button class="builder-back-btn" id="builder-back">${chevronLeftIcon} ${this.mode === 'entries' ? 'Back to Entries' : 'Back to formulas'}</button>
      ${companyBanner}
      <div class="builder-header">
        <span class="formula-type-badge badge-${f.functionType}">${typeBadge}</span>
        <h3 class="builder-title">${this.escapeHtml(f.displayName || f.name)}</h3>
        <code class="builder-fn-name">${fnName}</code>
        ${f.description ? `<p class="builder-desc">${this.escapeHtml(f.description)}</p>` : ''}
      </div>

      <div class="builder-params">
        ${paramsHtml || '<p class="hint-text">This formula has no parameters.</p>'}
      </div>

      ${this.showsViews() ? '<div class="builder-views" id="builder-views"></div>' : ''}

      <div class="builder-preview-section">
        <label class="builder-param-label">Preview</label>
        <code class="builder-preview" id="builder-preview">${this.previewHtml()}</code>
        ${this.mode === 'entries'
          ? '<p class="formula-rows-hint">Spills the rows behind the number into the cells below and to the right, which must be empty.</p>'
          : `<p class="formula-rows-hint">Tip: use <code>${builderFunctionName('entries')}</code> with the same arguments to see the matching rows</p>`}
      </div>

      <div class="builder-actions">
        <button class="ms-Button ms-Button--primary" id="builder-insert">
          <span class="ms-Button-label">Insert into cell</span>
        </button>
        <button class="ms-Button" id="builder-copy">
          <span class="ms-Button-label">Copy formula</span>
        </button>
      </div>
    `;

    this.attachEvents();
    this.mountViewPanel();
  }

  /** Customer and vendor Entries get the balance date and the saved views. */
  private showsViews(): boolean {
    return this.mode === 'entries' && !!this.viewsClient && !!this.formula && hasViews(this.formula.functionType);
  }

  private mountViewPanel(): void {
    this.viewPanel = null;
    const host = this.container.querySelector<HTMLElement>('#builder-views');
    if (!host || !this.viewsClient || !this.formula) return;
    this.viewPanel = new EntriesViewPanel(host, this.viewsClient, this.formula.id, name => {
      this.viewName = name;
      this.updatePreview();
    });
    void this.viewPanel.load().then(() => this.updateBalanceDate());
  }

  /** The year and month the user has typed, as the balance date the panel shows. */
  private updateBalanceDate(): void {
    if (!this.viewPanel || !this.formula) return;
    const args = formulaArguments(this.formula);
    const valueOf = (type: string) => this.paramValues[args.find(p => p.dateComponentType === type)?.name ?? ''] ?? '';
    this.viewPanel.setBalanceDate(balanceDateLabel(valueOf('year'), valueOf('month')));
  }

  private attachEvents(): void {
    const backBtn = this.container.querySelector('#builder-back');
    backBtn?.addEventListener('click', () => {
      this.activePickerParam = null;
      this.onBack();
    });

    const inputs = this.container.querySelectorAll<HTMLInputElement>('.builder-input');
    inputs.forEach(input => {
      input.dataset.originalPlaceholder = input.placeholder;
      input.addEventListener('focus', () => {
        this.focusedParam = input.dataset.param ?? null;
        this.updatePreview();
      });
      input.addEventListener('blur', () => {
        this.focusedParam = null;
        this.updatePreview();
      });
      input.addEventListener('input', () => {
        const param = input.dataset.param;
        if (param) {
          if (!input.value) {
            input.placeholder = input.dataset.originalPlaceholder || '';
          }
          this.paramValues[param] = input.value;
          this.updatePreview();
        }
      });
    });

    const modeBtns = this.container.querySelectorAll<HTMLButtonElement>('.builder-mode-btn');
    modeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const param = btn.dataset.param;
        if (param) this.toggleReferenceMode(param);
      });
    });

    const refBtns = this.container.querySelectorAll<HTMLButtonElement>('.builder-ref-btn');
    refBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const param = btn.dataset.param;
        if (param) this.activatePicker(param);
      });
    });

    const insertBtn = this.container.querySelector('#builder-insert');
    insertBtn?.addEventListener('click', () => this.insertFormula());

    const copyBtn = this.container.querySelector('#builder-copy');
    copyBtn?.addEventListener('click', () => this.copyFormula());
  }

  private updatePreview(): void {
    const el = this.container.querySelector('#builder-preview');
    if (el) el.innerHTML = this.previewHtml();
    this.updateBalanceDate();
  }

  /** The preview with each argument tagged, so the one being edited can be highlighted. */
  private previewHtml(): string {
    if (!this.formula) return '';
    const fnName = builderFunctionName(this.mode, this.formula.functionType);
    const slots = previewSlots(this.formula, this.paramValues, this.paramRefs, this.mode === 'entries' ? this.viewName : null).map(slot => {
      const text = this.escapeHtml(slot.text);
      if (!slot.param) return text;
      const cls = `builder-preview-slot${slot.empty ? ' is-empty' : ''}${slot.param === this.focusedParam ? ' is-active' : ''}`;
      return `<span class="${cls}" data-param="${this.escapeHtml(slot.param)}">${text}</span>`;
    });
    return `=${this.escapeHtml(fnName)}(${slots.join(',')})`;
  }

  private async insertFormula(): Promise<void> {
    if (!this.formula) return;
    this.activePickerParam = null;
    const formulaText = buildPreview(this.formula, this.paramValues, this.paramRefs, this.mode, this.viewName);

    try {
      await Excel.run(async (context) => {
        const cell = context.workbook.getSelectedRange();
        cell.formulas = [[formulaText]];
        await context.sync();
      });
      AccessibilityManager.getInstance().announceSuccess('Formula inserted into cell');
    } catch (error) {
      console.error('Insert formula error:', error);
      AccessibilityManager.getInstance().announceError('Failed to insert formula');
    }
  }

  private async copyFormula(): Promise<void> {
    if (!this.formula) return;
    const formulaText = buildPreview(this.formula, this.paramValues, this.paramRefs, this.mode, this.viewName);

    try {
      await navigator.clipboard.writeText(formulaText);
      AccessibilityManager.getInstance().announceSuccess('Formula copied to clipboard');
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = formulaText;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      AccessibilityManager.getInstance().announceSuccess('Formula copied to clipboard');
    }
  }

  private toggleReferenceMode(param: string): void {
    if (this.paramRefs.has(param)) {
      this.paramRefs.delete(param);
    } else {
      this.paramRefs.add(param);
    }
    this.syncReferenceUI(param);
    this.updatePreview();
  }

  private syncReferenceUI(param: string): void {
    const isRef = this.paramRefs.has(param);
    const input = this.container.querySelector<HTMLInputElement>(`.builder-input[data-param="${param}"]`);
    const btn = this.container.querySelector<HTMLButtonElement>(`.builder-mode-btn[data-param="${param}"]`);
    input?.classList.toggle('has-ref', isRef);
    if (btn) {
      btn.classList.toggle('is-ref', isRef);
      btn.textContent = isRef ? 'A1' : '""';
      btn.title = isRef
        ? 'Treated as a cell reference — click for text'
        : 'Treated as text — click for a cell reference';
    }
  }

  private activatePicker(param: string): void {
    if (this.activePickerParam) {
      this.clearPickerUI(this.activePickerParam);
    }
    this.activePickerParam = param;

    const btn = this.container.querySelector<HTMLButtonElement>(`.builder-ref-btn[data-param="${param}"]`);
    const input = this.container.querySelector<HTMLInputElement>(`.builder-input[data-param="${param}"]`);
    btn?.classList.add('picking');
    if (input) {
      input.placeholder = 'Click a cell...';
      input.classList.add('picking');
    }

    this.ensureSelectionListener();
  }

  private handleCellSelected(param: string, address: string): void {
    const cleanAddress = address.includes('!') ? (address.split('!')[1] ?? address) : address;

    this.paramValues[param] = cleanAddress;
    this.paramRefs.add(param);

    const input = this.container.querySelector<HTMLInputElement>(`.builder-input[data-param="${param}"]`);
    if (input) input.value = cleanAddress;
    this.syncReferenceUI(param);

    this.clearPickerUI(param);
    this.updatePreview();
  }

  private clearPickerUI(param: string): void {
    const btn = this.container.querySelector<HTMLButtonElement>(`.builder-ref-btn[data-param="${param}"]`);
    const input = this.container.querySelector<HTMLInputElement>(`.builder-input[data-param="${param}"]`);
    btn?.classList.remove('picking');
    if (input) {
      input.classList.remove('picking');
      input.placeholder = input.dataset.originalPlaceholder || '';
    }
  }

  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}
